import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeftIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  NoSymbolIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import ProviderNewsModal from "./ProviderNewsModal";
import { formatDateTime } from "../../../globalFunctions/DateTimeDisplayCleaner";
import NewsArticleContent from "../../../pages/News/NewsArticleContent";
import { ClockIcon } from "@heroicons/react/24/solid";
import Button from "../../../components/controls/Button";

const safeParseTiptapJsonString = (value) => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed && parsed.type === "doc") return parsed;
    return null;
  } catch {
    return null;
  }
};

const extractTextFromTiptap = (raw, maxLength = 120) => {
  if (!raw) return "";

  const parsed = safeParseTiptapJsonString(raw);
  if (!parsed || !Array.isArray(parsed.content)) {
    return String(raw || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, maxLength);
  }

  const parts = [];
  const walk = (node) => {
    if (!node || parts.join(" ").length >= maxLength) return;

    if (node.type === "text" && node.text) {
      parts.push(node.text);
      return;
    }

    if (Array.isArray(node.content)) node.content.forEach(walk);
  };

  parsed.content.forEach(walk);

  return parts.join(" ").replace(/\s+/g, " ").trim().slice(0, maxLength);
};

export default function ProviderNewsPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  useEffect(() => {
    loadNews();
  }, [userId]);

  const loadNews = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/News/Provider`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": userId,
          },
        }
      );
      if (!res.ok) throw new Error("Erreur");
      const data = await res.json();
      setNews(data);
    } catch {
      setError("Erreur lors du chargement.");
    } finally {
      setLoading(false);
    }
  };

  const submitForValidation = async (id) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/${id}/status?status=pending`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": userId,
        },
      }
    );

    if (!res.ok) {
      const msg = await res.text();
      return alert("Erreur : " + msg);
    }

    alert("Actualité soumise pour validation.");
    loadNews();
  };

  const handleAddNews = () => {
    setSelectedNews(null);
    setIsOpenModal(true);
  };

  const handleSave = () => {
    loadNews();
    setIsOpenModal(false);
  };

  const toggleVisibility = async (id) => {
    await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/${id}/toggle-visibility`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": userId,
        },
      }
    );
    loadNews();
  };

  const filtered = useMemo(() => {
    return news.filter((n) =>
      (n.title || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [news, search]);

  const getStatusLabel = (status) => {
    if (status === "pending") return "En attente";
    if (status === "accepted") return "Accepté";
    if (status === "refused") return "Refusée";
    if (status === "canceled") return "Annulée";
    return "Brouillon";
  };

  function getStatusIcon(status) {
    switch (status) {
      case "pending":
        return <ClockIcon className="w-5 h-5 text-gray-600" />;
      case "accepted":
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case "refused":
        return <XCircleIcon className="w-5 h-5 text-red-600" />;
      case "canceled":
        return <NoSymbolIcon className="w-5 h-5 text-gray-500" />;
      default:
        return <PaperAirplaneIcon className="w-5 h-5 text-gray-700" />;
    }
  }

  function getSubmitButtonClasses(status) {
    if (status === "draft")
      return "p-2 rounded-full transition hover:bg-primary hover:text-white";
    return "p-2 rounded-full cursor-not-allowed";
  }

  return (
    <div className="p-6 space-y-6 text-primary">
      <button
        onClick={() => navigate(-1)}
        className="btn bg-primary text-white rounded-full flex items-center gap-2"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        Retour
      </button>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold">Mes actualités</h1>

        <div className="flex w-full md:w-auto gap-2">
          <input
            type="text"
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input input-bordered w-full md:w-64"
          />

          <button
            onClick={handleAddNews}
            className="btn bg-primary text-white rounded-full"
          >
            Ajouter une actualité
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-600">
        Toutes les actualités seront vérifiées par notre équipe avant d'être
        publiées.
      </p>

      {loading ? (
        <div className="text-center">Chargement...</div>
      ) : error ? (
        <div className="text-center text-red-500">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-500">
          Aucune actualité trouvée.
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-lg bg-base-100 shadow-sm">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Titre</th>
                <th>Description</th>
                <th>Date publication</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const description = extractTextFromTiptap(item.text || "", 120);
                return (
                  <tr key={item.id}>
                    <td>{item.title}</td>

                    <td className="max-w-xs truncate" title={description}>
                      {description ? `${description}...` : "-"}
                    </td>

                    <td>
                      {formatDateTime(item.publishDate, true)
                        ? formatDateTime(item.publishDate, true)
                        : "-"}
                    </td>

                    <td>
                      <span className="badge badge-info">
                        {getStatusLabel(item.status)}
                      </span>
                    </td>

                    <td className="flex gap-3">
                      <button
                        onClick={() => setPreviewItem(item)}
                        className="p-2 rounded-full hover:bg-gray-100 transition"
                        title="Aperçu"
                      >
                        <MagnifyingGlassIcon className="w-5 h-5 text-gray-700" />
                      </button>

                      <button
                        onClick={() => submitForValidation(item.id)}
                        disabled={item.status !== "draft"}
                        className={getSubmitButtonClasses(item.status)}
                        title={getStatusLabel(item.status)}
                      >
                        {getStatusIcon(item.status)}
                      </button>

                      <button
                        onClick={() => toggleVisibility(item.id)}
                        className="p-2 rounded-full hover:bg-gray-100 transition"
                        title={item.isActive ? "Ne plus afficher" : "Afficher"}
                      >
                        {item.isActive ? (
                          <EyeSlashIcon className="w-5 h-5 text-gray-700" />
                        ) : (
                          <EyeIcon className="w-5 h-5 text-gray-700" />
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {isOpenModal && (
        <ProviderNewsModal
          isOpen={isOpenModal}
          onClose={() => setIsOpenModal(false)}
          onSave={handleSave}
          newsItem={selectedNews}
          userId={userId}
        />
      )}

      {previewItem && (
        <div className="modal modal-open modal-middle">
          <dialog open className="modal-box max-w-4xl p-0">
            <div className="p-6">
              <NewsArticleContent
                article={{
                  title: previewItem.title,
                  text: previewItem.text,
                  imageUrl: previewItem.imageUrl,
                  publishDate: previewItem.publishDate,
                  companyName: "Ma compagnie",
                }}
              />
            </div>

            <div className="modal-action p-4">
              <Button
                className="btn btn-outline rounded-full"
                onClick={() => setPreviewItem(null)}
              >
                Fermer
              </Button>
            </div>
          </dialog>
        </div>
      )}
    </div>
  );
}
